import { clsx } from "clsx";
import MaterialIcon, { Icon } from "src/shared/components/MaterialIcon";
import styles from "./PlaybackAction.module.css";

type PlaybackActionProps = {
  icon: Icon;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  /** Accessible name for screen readers (icon-only button). */
  "aria-label": string;
  /** Required for analytics — see https://www.notion.so/classdojo/Automatic-Product-Events-for-Web-bfc580f10a914c3ba514e5ec20f8ef9e?pvs=4 */
  "data-name": string;
  /** Variant styling lives in the parent's CSS Module (was the previous `innerSx` prop). */
  className?: string;
};

/**
 * 64x64 round playback-action button used inside the WatchVideo controls row.
 * Pre-migration this rendered as `<div onClick>` (no keyboard support, no
 * button semantics, missing accessible name) — converted to a real `<button>`
 * with required `aria-label` + `data-name`.
 *
 * Kept as a raw `<button>` rather than DDSIconButton because the icon child is
 * a `<MaterialIcon>` (toolkit-shared Material Icons font glyph), not a DDS
 * icon — DDSIconButton's sizing is tuned for the DDS svg-icon set and the
 * variant palette (primary/destructive/etc.) doesn't include the original
 * red-with-white play/pause appearance. The visual was custom anyway; this
 * change just promotes it to a proper button.
 */
export default function PlaybackAction({
  icon,
  onClick,
  className,
  "aria-label": ariaLabel,
  "data-name": dataName,
}: PlaybackActionProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      data-name={dataName}
      className={clsx(styles.action, className)}
    >
      <MaterialIcon icon={icon} />
    </button>
  );
}
